<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title>
        <span class="headline">{{ title }}</span>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>

        <v-form ref="form" v-model="valid" @submit.prevent="save">
          <p v-if="isEditing" class="text-caption text--secondary">
            As credenciais não são exibidas por segurança. Deixe os campos de
            credencial em branco para manter as atuais.
          </p>

          <!-- ZAPI -->
          <template v-if="type === 'ZAPI_WHATSAPP'">
            <v-text-field
              v-model="zapi.instanceId"
              label="Instance ID"
              :rules="reqIfFilling"
              outlined
              dense
            />
            <v-text-field
              v-model="zapi.instanceToken"
              label="Instance Token"
              :rules="reqIfFilling"
              outlined
              dense
            />
            <v-text-field
              v-model="zapi.clientToken"
              label="Client-Token (opcional)"
              outlined
              dense
            />
          </template>

          <!-- SMTP -->
          <template v-else-if="type === 'SMTP_EMAIL'">
            <v-row dense>
              <v-col cols="8">
                <v-text-field
                  v-model="smtp.host"
                  label="Host SMTP"
                  :rules="reqIfFilling"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model.number="smtp.port"
                  label="Porta"
                  type="number"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
            <v-switch
              v-model="smtp.secure"
              label="Conexão segura (TLS direto)"
              dense
              hide-details
              class="mt-0 mb-2"
            />
            <v-text-field
              v-model="smtp.username"
              label="Usuário"
              :rules="reqIfFilling"
              outlined
              dense
            />
            <v-text-field
              v-model="smtp.password"
              label="Senha"
              type="password"
              :rules="reqIfFilling"
              outlined
              dense
            />
            <v-text-field
              v-model="smtp.fromAddress"
              label="E-mail remetente (fromAddress)"
              :rules="emailRules"
              outlined
              dense
            />
            <v-text-field
              v-model="smtp.fromName"
              label="Nome remetente (opcional)"
              outlined
              dense
            />
          </template>

          <!-- Escopo -->
          <v-divider class="my-3" />
          <v-switch
            v-model="companyWide"
            label="Disponível para toda a empresa"
            dense
            hide-details
            class="mt-0 mb-2"
          />
          <platform-selector
            v-if="!companyWide"
            v-model="platform"
            label="Plataforma"
            placeholder="Selecione a plataforma"
            clearable
          />
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="saving">Cancelar</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ isEditing ? "Salvar" : "Criar" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import MESSAGER from "@/services/admin/messager.js";
import PlatformSelector from "@/components/global/PlatformSelector.vue";

const emptyZapi = () => ({ instanceId: "", instanceToken: "", clientToken: "" });
const emptySmtp = () => ({
  host: "",
  port: 587,
  secure: false,
  username: "",
  password: "",
  fromAddress: "",
  fromName: "",
});

export default {
  name: "AccountFormDialog",
  components: { PlatformSelector },
  props: {
    value: { type: Boolean, default: false },
    type: { type: String, default: "ZAPI_WHATSAPP" },
    account: { type: Object, default: null },
  },
  data: () => ({
    valid: false,
    saving: false,
    error: null,
    zapi: emptyZapi(),
    smtp: emptySmtp(),
    companyWide: true,
    platform: null,
    emailRules: [
      (v) =>
        !v ||
        /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ||
        "E-mail inválido",
    ],
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    isEditing() {
      return !!this.account;
    },
    title() {
      const label =
        this.type === "SMTP_EMAIL" ? "Conta de E-mail" : "Conta ZAPI";
      return `${this.isEditing ? "Editar" : "Nova"} ${label}`;
    },
    // Em edicao, credenciais sao opcionais (mantem as atuais se em branco).
    reqIfFilling() {
      if (this.isEditing) return [];
      return [(v) => !!v || "Campo obrigatório"];
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      this.error = null;
      this.zapi = emptyZapi();
      this.smtp = emptySmtp();
      this.platform = null;
      this.companyWide = this.account ? !this.account.platformId : true;
      if (this.account && this.account.platformId) {
        this.platform = { id: this.account.platformId };
      }
      this.$refs.form && this.$refs.form.resetValidation();
    },
    close() {
      this.dialog = false;
    },
    buildCredentials() {
      if (this.type === "ZAPI_WHATSAPP") {
        const filled = this.zapi.instanceId || this.zapi.instanceToken;
        if (!filled && this.isEditing) return undefined;
        return {
          instanceId: this.zapi.instanceId,
          instanceToken: this.zapi.instanceToken,
          clientToken: this.zapi.clientToken || undefined,
        };
      }
      // SMTP
      const filled =
        this.smtp.host || this.smtp.username || this.smtp.password;
      if (!filled && this.isEditing) return undefined;
      return {
        host: this.smtp.host,
        port: this.smtp.port,
        secure: this.smtp.secure,
        username: this.smtp.username,
        password: this.smtp.password,
        fromAddress: this.smtp.fromAddress,
        fromName: this.smtp.fromName || undefined,
      };
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      const platformId = this.companyWide ? null : this.platform?.id || null;
      if (!this.companyWide && !platformId) {
        this.error = "Selecione uma plataforma ou marque toda a empresa";
        return;
      }

      const credentials = this.buildCredentials();
      try {
        this.saving = true;
        this.error = null;
        if (this.isEditing) {
          const payload = { platformId };
          if (credentials) payload.credentials = credentials;
          await MESSAGER.accounts.update(this.account.id, payload);
        } else {
          await MESSAGER.accounts.create({
            type: this.type,
            credentials,
            platformId,
          });
        }
        this.close();
        this.$emit("saved");
      } catch (error) {
        this.error = error.message || "Erro ao salvar conta";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
